import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals } from '../globals';
import { CustomerBillData } from './customer-bill.service';

export interface PurchaseOrder {
  active: number;
  createdAt: any;
  date: string;
  folio: number;
  notes?: string;
  supplierId: any;
  fiscalDataId: any;
  //productionOrderId: any;
  prForOrders?: PrForOrders[];
  prForOrdersToDelete?: PrForOrders[];
  subTotal: number;
  ivaTotal: number;
  total: number;
  updatedAt: any;
  deliveryRequiredDate: Date;
  currency: keyof CustomerBillData['currencySelect'];
  change: number;
  canceled: boolean;
  cancelReasons: {datetime: string, msg: string}[];
}

export interface PrForOrders {
  uniqueId: string; // identifier for the object
  id?: string; // id of the product being referenced
  code: string;
  description: string;
  name: string;
  customName: string;
  quantity: number;
  inWareQuantity: number;
  billedQuantity: number;
  pricePerUnit: number;
  amount: number;
  amountIva: number;
  usesIva: boolean;
  odpId: string;
  odpName: string;
  customerName: string;
}

export interface PurchaseOrderId extends PurchaseOrder {
  id: string;
}

@Injectable()
export class PurchaseOrderService {

  purchaseOrderCollection: AngularFirestoreCollection<PurchaseOrder>;
  purchaseOrders: any;

  purchaseOrderDocument: AngularFirestoreDocument<PurchaseOrderId>;
  purchaseOrder: Observable<PurchaseOrderId>;

  collection: string;
  supplierCollection: string;
  fiscalDataCollection: string;
  productionOrderCollection: string;

  // NOTE: Change this any for PurchaseOrder Interface
  purchaseOrder$: Observable<any>;
  purchaseOrders$: Observable<any[]>;

  constructor(
    private afs: AngularFirestore,
    private afh: FirestoreService,
    public router: Router,
  ) {
    this.collection = Globals.PURCHASE_ORDER_COLLECTION;
    this.supplierCollection = Globals.CUSTOMER_SUPPLIER_COLLECTION;
    this.fiscalDataCollection = Globals.FISCAL_DATA_COLLECTION;
    this.productionOrderCollection = Globals.PRODUCTION_ORDER_COLLECTION;

  }

  /**
   * Get all purchaseOrders with references
   * @return {Observable} Arrays of purchaseOrders
   */
  getPurchaseOrders({filterDays=undefined, date='', onEmpty=null}={}) {
    let col;
    if(filterDays){
      let filteredDate = new Date();
      filteredDate.setDate(filteredDate.getDate() - filterDays);
      let date = this.stringifyDate(filteredDate);
      col = this.afh.colWithIds$(this.collection, ref => ref.where('active', '==', true).where('date', '>=', date) );
    }else if(date){
      col = this.afh.colWithIds$(this.collection, ref => ref.where('active', '==', true).where('date', '>=', date) );
    }else{
      col = this.afh.colWithIds$(this.collection, ref => ref.where('active', '==', true) );
    }

    return col.map(documents => {
      if(!documents.length && onEmpty) onEmpty();
      return documents.map(document => {
        const supplierRef = this.afh.docWithIds$(`${document.supplierId.path}`);
        const fiscalDataRef = this.afh.docWithIds$(`${document.fiscalDataId.path}`);
        return Observable.combineLatest(
          supplierRef,
          fiscalDataRef,
          (supplierData, fiscalDataData) => {
            return {
              supplier: supplierData,
              fiscalData: fiscalDataData,
            };
        })
        .map(data => {
          return { supplierObj: data.supplier, fiscalDataObj: data.fiscalData, ...document };
        });
      });
    })
    .mergeMap(purchaseOrdersDocuments => Observable.combineLatest(purchaseOrdersDocuments));
  }

  /**
  * Get purchaseOrder data
  * @param {string} sid - The id of the purchaseOrder
  * @return {Observable} The PurchaseOrder Object
  */
  getPurchaseOrder(sid: string) {
    return this.purchaseOrder$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {
      const supplierRef = this.afh.docWithIds$(document.supplierId.path);
      const fiscalDataRef = this.afh.docWithIds$(`${document.fiscalDataId.path}`);
      return Observable.combineLatest(
        supplierRef,
        fiscalDataRef,
        (supplierData, fiscalDataData ) => {
          return {
            suppliers: supplierData,
            fiscalData: fiscalDataData,
          };
      })
      .map(data => {
        return { 
          supplierObj: data.suppliers,
          fiscalDataObj: data.fiscalData,
          ...document
        };
      });
    })
    .mergeMap(purchaseOrdersDocuments => Observable.combineLatest(purchaseOrdersDocuments));
  }


  /**
  * Create new purchaseOrder
  * @param {PurchaseOrder} purchaseOrder - The data of the purchaseOrder
  */
  addPurchaseOrder(purchaseOrder: PurchaseOrder) {
    purchaseOrder.supplierId = this.afh.doc(`${this.supplierCollection}/${purchaseOrder.supplierId}`).ref;
    purchaseOrder.fiscalDataId = this.afh.doc(`${this.fiscalDataCollection}/${purchaseOrder.fiscalDataId}`).ref;
    return this.afh.add(`${this.collection}`, purchaseOrder);
  }

  /**
   * Update current purchaseOrder
   * @param {string} sid  - The id of the purchaseOrder
   * @param {PurchaseOrder} purchaseOrder - The data of the purchaseOrder
   */
  updatePurchaseOrder(sid: string, purchaseOrder: Partial<PurchaseOrder>) {
    
    return this.afh.update(`${this.collection}/${sid}`, purchaseOrder);    
  }

  updateSupplierAndFiscal(id: string, {supplierId, fiscalDataId}){
    let obj = {
      supplierId: this.afh.doc(`${this.supplierCollection}/${supplierId}`).ref,
      fiscalDataId: this.afh.doc(`${this.fiscalDataCollection}/${fiscalDataId}`).ref
    }
    this.updatePurchaseOrder(id, obj);
  }

  /**
   * Delete current purchaseOrder
   * @param {string} sid - The id of the purchaseOrder
   */
  deletePurchaseOrder(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }

  stringifyDate(date: Date){
    var dd:string = date.getDate() + '';
    var mm:string = date.getMonth()+ 1 + '';
    var yyyy = date.getFullYear();
    if(+dd<10) dd = '0'+dd;
    if(+mm<10) mm = '0'+mm;
    return yyyy + '-' + mm + '-' + dd;
  }

}

export class PurchaseOrderFilter {

  allRows: any[];

  dateIndex: number;
  supplierIndex: number;
  odpIndex: number;
  customerIndex: number;
  folioStr: string;

  constructor(allRows: any[], {dateIndex=0,supplierIndex=0, odpIndex=0, customerIndex=0, folioStr=''}){
    this.allRows = allRows.sort(({name}, {folio}) => name < folio ? 1 : -1);
    this.dateIndex = dateIndex;
    this.supplierIndex = supplierIndex;
    this.odpIndex = odpIndex;
    this.customerIndex = customerIndex;
    this.folioStr = folioStr;

    let suppliers = new Set( allRows.map(row => row.supplierName) );
    let sortedSuppliers = [...suppliers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.supplierSelect = ['Todos', ...sortedSuppliers].map(name => ({name}) );
    
    let odps = new Set( allRows.reduce((acc, cur) => [...acc, ...cur.odpNames], []) );
    let sortedOdps = [...odps].sort((it1, it2) => it1 < it2 ? 1 : -1);
    this.odpSelect = ['Todos', ...sortedOdps].map(name => ({name}) );
    
    let customers = new Set( allRows.reduce((acc, cur) => [...acc, ...cur.customerNames], []) );
    let sortedcustomers = [...customers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.customerSelect = ['Todos', ...sortedcustomers].map(name => ({name}) );
  }

  get filteredData() {
    return this.allRows
      .filter(({date}) => {
        let rowDate = new Date(date);
        let filterDate = new Date();
        let {amount, unit} = this.dateSelect[this.dateIndex];
        switch(unit){
          case 'day': filterDate.setDate(filterDate.getDate() - amount); break;
          case 'month': filterDate.setMonth(filterDate.getMonth() - amount); filterDate.setDate(0); break;
          case 'year': filterDate = new Date(filterDate.getFullYear() - amount, 0); break;
          default: filterDate.setFullYear(2015); break;
        }
        if(rowDate >= filterDate) return 1;
      })
      .filter(({supplierName: rowName}) => {
        let {name: filterName} = this.supplierSelect[this.supplierIndex];
        if(rowName == filterName || filterName == 'Todos') return 1;
      })
      .filter(({odpNames: rowNames}) => {
        let {name: filterName} = this.odpSelect[this.odpIndex];
        if(rowNames.includes(filterName) || filterName == 'Todos') return 1;
      })
      .filter(({customerNames: rowNames}) => {
        let {name: filterName} = this.customerSelect[this.customerIndex];
        if(rowNames.includes(filterName) || filterName == 'Todos') return 1;
      })
      .filter(({name: rowName}) => {
        if(rowName == this.folioStr || !this.folioStr) return 1;
      });
  }

  get json(): string {
    return JSON.stringify({
      dateIndex: this.dateIndex,
      supplierIndex: this.supplierIndex,
      folioStr: this.folioStr
    });
  }

  dateSelect = [
    { amount: 15, unit: 'day', name: '15 días' },
    { amount: 0, unit: 'month', name: 'Este mes' },
    { amount: 6, unit: 'month', name: '6 meses' },
    { amount: 0, unit: 'year', name: 'Este año' },
    { amount: 0, unit: 'forever', name: 'Siempre' },
  ];
  supplierSelect = [];
  odpSelect = [];
  customerSelect = [];
}
